import { Award, GraduationCap, ShieldCheck, Shield, FileCheck2, Briefcase, BookCheck, ScrollText, CheckCircle2, Languages } from "lucide-react";
import { CREDENTIALS, CULTURAL_SAFETY, NAVICA } from "@/lib/constants";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";

const iconMap = { Award, GraduationCap, ShieldCheck, Shield, FileCheck2, Briefcase, BookCheck, ScrollText };

export default function Trust() {
  usePageMeta({
    title: "Trust & Credentials, AASW, NDIS Registration & Insurance",
    description: "AASW accreditation, NDIS provider registration, insurance and cultural safety commitments at Navica Supports.",
  });
  return (
    <div data-testid="trust-page">
      {/* HERO */}
      <section className="pt-16 lg:pt-24 pb-16 relative overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#EA823A] font-semibold mb-5 fade-up">Trust, credentials & cultural safety</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#1F2A2E] leading-[1.05] mb-6 fade-up fade-up-1">
            How we <span className="italic" style={{ color: "var(--navica-coral)" }}>show our work</span>.
          </h1>
          <p className="text-xl text-[#4A595E] leading-relaxed max-w-3xl fade-up fade-up-2">
            Trust is the currency of social work. This page gathers the credentials, compliance, insurance and cultural safety commitments that sit behind every Navica Supports engagement.
          </p>
        </div>
      </section>

      {/* CREDENTIALS GRID */}
      <section className="pb-20" data-testid="credentials-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-display text-3xl md:text-4xl text-[#1F2A2E]">Credentials & compliance</h2>
            <span className="text-xs uppercase tracking-wider text-[#6B7A80]">Current as at {new Date().toLocaleString("en-AU", { month: "long", year: "numeric" })}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CREDENTIALS.map((c, idx) => {
              const Icon = iconMap[c.icon];
              return (
                <div
                  key={c.title}
                  className="bg-white border border-[#F0E5DE] rounded-3xl p-6 hover:border-[#F4C3CC] transition fade-up"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                  data-testid={`credential-${idx}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-[#FCE7EB] flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#E2718A]" />
                  </div>
                  <h3 className="font-display text-lg text-[#1F2A2E] mb-2 leading-snug">{c.title}</h3>
                  <p className="text-sm text-[#4A595E] leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BUSINESS & REGISTRATION DETAILS */}
      <section className="pb-20" data-testid="registration-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="bg-[#284952] text-white rounded-sm p-8 lg:p-12 relative overflow-hidden">
            <div className="relative grid md:grid-cols-2 gap-10">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#F4C3CC] font-semibold mb-3">Business details</div>
                <h2 className="font-display text-3xl lg:text-4xl mb-6 leading-tight">For institutional referrers.</h2>
                <p className="text-white/80 leading-relaxed">
                  These are the key business and compliance details often required by NDIA, plan managers and partner organisations.
                </p>
              </div>
              <dl className="space-y-5">
                <Row label="Legal name" value={NAVICA.legal_name} />
                <Row label="Trading as" value={NAVICA.name} />
                <Row label="ABN" value={NAVICA.abn} testId="reg-abn" />
                <Row label="NDIS Provider Status" value={NAVICA.ndis_provider_status} />
                <Row label="NDIS Provider #" value={NAVICA.ndis_provider_number} />
                <Row label="Registered office" value={`${NAVICA.address}, ${NAVICA.city}`} />
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* CULTURAL SAFETY */}
      <section className="py-20 bg-[#F1E4D5]" data-testid="cultural-safety-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <Languages className="w-5 h-5 text-[#E2718A]" />
              <span className="text-sm uppercase tracking-[0.2em] text-[#EA823A] font-semibold">Cultural safety in practice</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl text-[#1F2A2E] leading-tight mb-6">
              Beyond an Acknowledgment, into <span className="italic" style={{ color: "var(--navica-coral)" }}>practice</span>.
            </h2>
            <p className="text-lg text-[#4A595E] leading-relaxed">
              {CULTURAL_SAFETY.intro}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {CULTURAL_SAFETY.pillars.map((p, idx) => (
              <div
                key={p.title}
                className="bg-white rounded-3xl p-7 border border-[#F0E5DE] fade-up"
                style={{ animationDelay: `${idx * 0.06}s` }}
                data-testid={`pillar-${idx}`}
              >
                <h3 className="font-display text-xl text-[#1F2A2E] mb-4 leading-snug">{p.title}</h3>
                <ul className="space-y-2.5">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3 text-sm text-[#4A595E] leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-[#E2718A] mt-0.5 shrink-0" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACKNOWLEDGMENT */}
      <section className="py-20" data-testid="acknowledgment-section">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="bg-white border border-[#F0E5DE] rounded-[2.5rem] p-8 lg:p-12">
            <div className="flex items-center gap-4 mb-5">
              <div className="flex items-center gap-2">
                <span className="inline-block w-9 h-6 rounded-sm" style={{ background: "linear-gradient(to bottom, #000 0 33%, #E2718A 33% 66%, #EA823A 66% 100%)" }} aria-label="Aboriginal flag" />
                <span className="inline-block w-9 h-6 rounded-sm bg-gradient-to-b from-emerald-700 via-white to-blue-700" aria-label="Torres Strait Islander flag" />
              </div>
              <div className="text-sm uppercase tracking-[0.2em] text-[#EA823A] font-semibold">Acknowledgment of Country</div>
            </div>
            <p className="text-lg text-[#284952] leading-relaxed mb-5">
              Navica Supports acknowledges Aboriginal and Torres Strait Islander peoples as the Traditional Custodians of the lands where we live and work, and the Dharug people on whose land we operate in Parramatta.
            </p>
            <p className="text-lg text-[#284952] leading-relaxed">
              We pay our respects to Elders past and present, and celebrate their enduring connection to land, culture and community. Sovereignty was never ceded. Always was, always will be Aboriginal land.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="trust-cta">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-[#1F2A2E] leading-tight mb-6">
            Need documentation for a referral or partnership?
          </h2>
          <p className="text-lg text-[#4A595E] leading-relaxed mb-8">
            We're happy to share Working with Children Check numbers, NDIS Worker Screening clearances, insurance certificates of currency and capability statements on request.
          </p>
          <Link to="/contact" className="btn-primary">Request documentation</Link>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 border-b border-white/10 pb-4 last:border-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wider text-[#F4C3CC] sm:w-44 shrink-0">{label}</dt>
      <dd className="text-white font-medium" data-testid={testId}>{value}</dd>
    </div>
  );
}
